import React from 'react';
import WallVideoSplitPreview from './WallVideoSplitPreview';
import TemplateItemPreview from './TemplateItemPreview';

/**
 * Smart Template Engine — Preview System (Phase 2A).
 *
 * Renders ONE playlist item across the wall grid, dispatching by content_type:
 *   - template  → native template render (TemplateItemPreview → WallPreviewRenderer)
 *   - full_wall → one media spanning the wall, split per cell (WallVideoSplitPreview)
 *   - media_file→ same media mirrored, filling the wall
 *   - split     → per-cell assigned media
 * Reused by the playlist player (2A) and simulator (4). Read-only, no sync contact.
 */

const VIDEO_EXT = ['.mp4', '.m3u8', '.webm', '.mkv', '.mov', '.avi', '.ts'];
export const isVideoUrl = (u) => !!u && VIDEO_EXT.some((e) => u.toLowerCase().includes(e));

function mediaUrl(mediaMap, id, fallback) {
  return mediaMap?.[id]?.path || mediaMap?.[id]?.thumb || fallback || null;
}

export default function WallItemPreview({ item, grid, mediaMap = {}, themes = [], defaultZones = {}, resolvePhoto, maxWidth = 520 }) {
  if (!item) return null;
  const cols = Math.max(1, grid?.cols || 1);
  const rows = Math.max(1, grid?.rows || 1);
  const portrait = grid?.orientation === 'portrait';
  const cellAspect = portrait ? 9 / 16 : 16 / 9;
  const bezel = 4;
  const cellW = (maxWidth - (cols - 1) * bezel) / cols;
  const cellH = cellW / cellAspect;

  if (item.content_type === 'template') {
    return (
      <TemplateItemPreview instanceId={item.template_instance_id} grid={grid}
        themes={themes} defaultZones={defaultZones} resolvePhoto={resolvePhoto} maxWidth={maxWidth} />
    );
  }

  if (item.content_type === 'full_wall') {
    const url = mediaUrl(mediaMap, item.media_file_id, item.file_url);
    if (!url) return <EmptyWall maxWidth={maxWidth} cellH={cellH} rows={rows} />;
    return <WallVideoSplitPreview src={url} grid={grid} maxWidth={maxWidth} fit={item.fit_mode || 'crop'}
      kind={isVideoUrl(url) ? 'video' : 'image'} />;
  }

  if (item.content_type === 'split') {
    return (
      // Physical screen layout — pin LTR so RTL never mirrors the cells.
      <div dir="ltr" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, ${cellW}px)`,
        gridTemplateRows: `repeat(${rows}, ${cellH}px)`, gap: bezel, background: '#000', padding: bezel,
        borderRadius: 10, width: 'fit-content' }}>
        {Array.from({ length: rows * cols }).map((_, i) => {
          const url = mediaUrl(mediaMap, item.screen_assignments?.[String(i + 1)]);
          return (
            <div key={i} style={{ width: cellW, height: cellH, overflow: 'hidden', borderRadius: 4, background: '#111' }}>
              {url ? (isVideoUrl(url)
                ? <video src={url} muted autoPlay loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />) : null}
            </div>
          );
        })}
      </div>
    );
  }

  // media_file — mirrored: the SAME media plays on every screen. Render one cell
  // per screen (all identical) WITH bezel gaps, so the preview reads as "N screens
  // each showing the same thing" — not one big flat image (which is indistinguishable
  // from a single 1×1 wall).
  const url = mediaUrl(mediaMap, item.media_file_id, item.file_url);
  if (!url) return <EmptyWall maxWidth={maxWidth} cellH={cellH} rows={rows} />;
  return (
    // Physical screen layout — pin LTR so RTL never mirrors the cells.
    <div dir="ltr" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, ${cellW}px)`,
      gridTemplateRows: `repeat(${rows}, ${cellH}px)`, gap: bezel, background: '#000', padding: bezel,
      borderRadius: 10, width: 'fit-content' }}>
      {Array.from({ length: rows * cols }).map((_, i) => (
        <div key={i} style={{ width: cellW, height: cellH, overflow: 'hidden', borderRadius: 4, background: '#111' }}>
          {isVideoUrl(url)
            ? <video src={url} muted autoPlay loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
        </div>
      ))}
    </div>
  );
}

function EmptyWall({ maxWidth, cellH, rows }) {
  return (
    <div style={{ width: maxWidth, height: cellH * rows, borderRadius: 10, background: '#111',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)' }}>
      <i className="pi pi-image" style={{ fontSize: 28 }} />
    </div>
  );
}
